import algosdk from "algosdk";
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useWallet } from "../context/WalletContext";
import { useTransaction } from "../context/TransactionContext";
import axios from "axios";
import "../styles/general.css";
import "../styles/quizz.css";

interface QuizGameProps {
  algodClient: algosdk.Algodv2;
  setMessage: (msg: string) => void;
}

const appId = 3117725730;
const indexerUrl = "https://mainnet-idx.algonode.cloud/v2";

interface Question {
  question: string;
  options: string[];
  answerIndex: number;
}

interface RankingEntry {
  address: string;
  score: number;
  time: number;
}

const originalQuestions: Question[] = [
  {
    question: "¿Cuál es el cultivo más producido en el mundo por volumen?",
    options: ["Trigo", "Maíz", "Arroz", "Papa"],
    answerIndex: 1,
  },
  {
    question: "¿Qué es un activo financiero?",
    options: [
      "Un terreno de cultivo",
      "Una herramienta de trabajo",
      "Un bien que genera ingresos o aumenta de valor",
      "Un préstamo bancario",
    ],
    answerIndex: 2,
  },
  {
    question: "¿Cuál de estos cultivos es usado como fuente de energía renovable?",
    options: ["Soya", "Caña de azúcar", "Tomate", "Lechuga"],
    answerIndex: 1,
  },
  {
    question: "¿Qué país es el mayor exportador de café en el mundo?",
    options: ["Etiopía", "Colombia", "Vietnam", "Brasil"],
    answerIndex: 3,
  },
  {
    question: "¿Qué representa la sigla “USDC”?",
    options: [
      "Criptomoneda estable vinculada al euro",
      "Token agrícola",
      "Stablecoin ligada al dólar estadounidense",
      "Crédito bancario digital",
    ],
    answerIndex: 2,
  },
  {
    question: "¿Qué función tiene la rotación de cultivos?",
    options: [
      "Mejorar el sabor de los productos",
      "Disminuir el uso de agua",
      "Evitar erosión y conservar fertilidad del suelo",
      "Aumentar la cantidad de plagas",
    ],
    answerIndex: 2,
  },
  {
    question: "¿Qué es una billetera digital (wallet)?",
    options: [
      "Una tarjeta bancaria",
      "Una app para guardar criptomonedas",
      "Un monedero con billetes",
      "Una cuenta bancaria",
    ],
    answerIndex: 1,
  },
  {
    question: "¿Cuál de estas criptomonedas es eficiente energéticamente?",
    options: ["Bitcoin", "Ethereum", "Algorand", "Litecoin"],
    answerIndex: 2,
  },
  {
    question: "¿Qué es la agroecología?",
    options: [
      "Uso de fertilizantes químicos",
      "Estudio del mar",
      "Producción agrícola sostenible con principios ecológicos",
      "Método para sembrar en macetas",
    ],
    answerIndex: 2,
  },
  {
    question: "¿Qué es una cooperativa agrícola?",
    options: [
      "Una finca con muchos cultivos",
      "Sociedad de campesinos que producen y venden juntos",
      "Banco rural",
      "Tipo de fertilizante",
    ],
    answerIndex: 1,
  },
];

const QuizGame: React.FC<QuizGameProps> = ({ algodClient, setMessage }) => {
  const { account } = useWallet();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [endTime, setEndTime] = useState<Date | null>(null);
  const [selected, setSelected] = useState<number | null>(null);
  const [completed, setCompleted] = useState(false);
  const [ranking, setRanking] = useState<RankingEntry[]>([]);
  const [isOptedIn, setIsOptedIn] = useState(false);
  const [, setUserTime] = useState<number | null>(null);
  const { openTransactionModal } = useTransaction();
  const [resultSent, setResultSent] = useState(false);
  const [shuffledQuestions, setShuffledQuestions] = useState<Question[]>([]);
  const navigate = useNavigate();

  const shuffleQuestions = (questions: Question[]): Question[] => {
  const shuffled = [...questions].sort(() => Math.random() - 0.5);
  return shuffled.map((q) => {
    const correct = q.options[q.answerIndex];
    const shuffledOptions = [...q.options].sort(() => Math.random() - 0.5);
    const newAnswerIndex = shuffledOptions.findIndex(opt => opt === correct);
    return {
      ...q,
      options: shuffledOptions,
      answerIndex: newAnswerIndex
    };
  });
};

  const fetchUserStats = useCallback(async () => {
    try {
      const info = await algodClient.accountInformation(account!.addr).do();
      const state = info["apps-local-state"]?.find((s: any) => s.id === appId);
      if (!state || !state["key-value"]) return;

      for (const kv of state["key-value"]) {
        const key = atob(kv.key);
        if (key === "ultimo_tiempo") setUserTime(kv.value.uint);
        if (key === "ultimo_puntaje") setStoredScore(kv.value.uint);
      }
    } catch (err) {
      console.error("Error al leer estado local:", err);
    }
  }, [account, algodClient]);

  const handleOptIn = async () => {
    if (!account) return;
    try {
      const info = await algodClient.accountInformation(account.addr).do();
      const optedIn = info["apps-local-state"]?.some((app: any) => app.id === appId);
      if (!optedIn) {
        const params = await algodClient.getTransactionParams().do();
        const txn = algosdk.makeApplicationOptInTxn(account.addr, params, appId);
        const signedTxn = txn.signTxn(account.sk);
        await algodClient.sendRawTransaction(signedTxn).do();
        setMessage("✅ Activación exitosa");
      } else {
        setMessage("✅ Nueva participación activa");
      }
      setIsOptedIn(true);
      await fetchUserStats();
    } catch (e: any) {
      console.error("Error durante el opt-in:", e);
      const errorMessage = e?.response?.data?.message || e?.message || "Error desconocido";
      setMessage(`❌ Error durante el opt-in: ${errorMessage}`);
    }
  };

  const handleAnswer = (index: number) => {
    if (selected !== null || completed) return;
    setSelected(index);
    if (!startTime) setStartTime(new Date());

    const isCorrect = shuffledQuestions[currentIndex].answerIndex === index;
    if (isCorrect) setScore((prev) => prev + 1);

    setTimeout(() => {
      if (currentIndex === shuffledQuestions.length - 1) {
        setEndTime(new Date());
        setCompleted(true);
        fetchRanking();
      } else {
        setCurrentIndex((prev) => prev + 1);
        setSelected(null);
      }
    }, 1000);
  };

  const submitResult = async () => {
    if (!account || !endTime || !startTime || resultSent) return;

    const rawDuration = (endTime.getTime() - startTime.getTime()) / 10;
    const duration = Math.floor(rawDuration);
    const scoreUint64 = algosdk.encodeUint64(score);
    const durationUint64 = algosdk.encodeUint64(duration);

    openTransactionModal({
      recipient: "Smart Contract",
      amount: 100,
      asset: { name: "UMQ", id: 1810650610, decimals: 9 },
      onConfirm: async () => {
        setResultSent(true);
        try {
          const suggestedParams = await algodClient.getTransactionParams().do();

          const feeTxn = algosdk.makeAssetTransferTxnWithSuggestedParamsFromObject({
            from: account.addr,
            to: algosdk.getApplicationAddress(appId),
            amount: 15e9,
            assetIndex: 1810650610,
            suggestedParams,
          });

          const paymentTxn = algosdk.makeAssetTransferTxnWithSuggestedParamsFromObject({
            from: account.addr,
            to: algosdk.getApplicationAddress(appId),
            amount: 100e9,
            assetIndex: 1810650610,
            suggestedParams,
          });

          const appCallTxn = algosdk.makeApplicationNoOpTxnFromObject({
            from: account.addr,
            appIndex: appId,
            appArgs: [
              new Uint8Array(Buffer.from("submit_result")),
              scoreUint64,
              durationUint64,
            ],
            foreignAssets: [1810650610],
            suggestedParams,
          });

          const txns = [feeTxn, paymentTxn, appCallTxn];
          algosdk.assignGroupID(txns);
          const signed = txns.map(txn => txn.signTxn(account.sk));
          const { txId } = await algodClient.sendRawTransaction(signed).do();
          setMessage(`✅ Resultado enviado. TxID: ${txId}`);
          fetchRanking();
          await fetchUserStats();
        } catch (err) {
          console.error(err);
          setMessage("❌ Error al enviar resultado");
        }
      },
    });
  };

  const fetchRanking = async () => {
    try {
      const res = await axios.get(`${indexerUrl}/accounts?application-id=${appId}&limit=1000`);
      const users = res.data.accounts.map((acc: any) => {
        let score = 0;
        let time = 9999;
        const state = acc["apps-local-state"]?.find((s: any) => s.id === appId);
        state?.["key-value"]?.forEach((kv: any) => {
          const key = atob(kv.key);
          if (key === "ultimo_puntaje") score = kv.value.uint;
          if (key === "ultimo_tiempo") time = kv.value.uint;
        });
        return { address: acc.address, score, time };
      });

      const sorted = users
        .filter((u: { score: number; }) => u.score > 0)
        .sort((a: { score: number; time: number; }, b: { score: number; time: number; }) => b.score - a.score || a.time - b.time);
      setRanking(sorted);
    } catch (error) {
      console.error("Error cargando ranking:", error);
    }
  };

  useEffect(() => {
    setShuffledQuestions(shuffleQuestions(originalQuestions));
  }, []);

  useEffect(() => {
  if (!account) return;
  fetchRanking(); 
  const interval = setInterval(fetchRanking, 10000);
  return () => clearInterval(interval);
}, [account]);

  if (!isOptedIn) {
    return (
      <div className="modal-container">
        <h2>Pon a prueba tus conocimientos</h2> 
        <h4>10 Preguntas sobre agro y finanzas</h4> 
        <p>Bienvenid@. Recompensa para los 3 primeros</p>
        <button className="button-primary" onClick={handleOptIn}>Activar Quiz</button>
        <button className="button-exit" onClick={() => navigate("/home")}>Volver</button>
         {resultSent && (
      <>
        <h2>🏆 Clasificación:</h2>
        <table className="ranking-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Dirección</th>
              <th>Puntaje</th>
              <th>Tiempo</th>
            </tr>
          </thead>
          <tbody>
            {ranking.map((r, i) => (
              <tr key={r.address}>
                <td>{i + 1}</td>
                <td>{r.address.slice(0, 6)}...{r.address.slice(-4)}</td>
                <td>{r.score}</td>
                <td>
                  {r.time === 9999 ? "—" : (() => {
                    const totalMs = r.time * 10;
                    const minutes = Math.floor(totalMs / 60000);
                    const seconds = Math.floor((totalMs % 60000) / 1000);
                    const centis = Math.floor((totalMs % 1000) / 10);
                    return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}.${centis.toString().padStart(2, "0")}`;
                  })()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </>
    )}
  </div>
)}

  if (!shuffledQuestions.length) {
    return <div className="modal-container"><h2>Cargando preguntas...</h2></div>;
  }

  const current = shuffledQuestions[currentIndex];

  if (completed) {
    return (
      <div className="modal-container">
        <h2>¡Juego terminado!</h2>
        <p>PUNTAJE: {Math.min(score, shuffledQuestions.length)} / {shuffledQuestions.length}</p>
        {startTime && endTime && (() => {
  const elapsed = endTime.getTime() - startTime.getTime();
  const minutes = Math.floor(elapsed / 60000);
  const seconds = Math.floor((elapsed % 60000) / 1000);
  const centiseconds = Math.floor((elapsed % 1000) / 10);

  return (
    <p>
      Tiempo: {`${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}.${centiseconds.toString().padStart(2, "0")}`} minutos
    </p>
  );
})()}
        <button onClick={submitResult} className="button-primary">Enviar resultado</button>
        <button onClick={() => navigate("/home")} className="button-exit">Volver</button>
         {resultSent && (
      <>
        <h2>🏆 Clasificación:</h2>
        <table className="ranking-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Dirección</th>
              <th>Puntaje</th>
              <th>Tiempo</th>
            </tr>
          </thead>
          <tbody>
            {ranking.map((r, i) => (
              <tr key={r.address}>
                <td>{i + 1}</td>
                <td>{r.address.slice(0, 6)}...{r.address.slice(-4)}</td>
                <td>{r.score}</td>
                <td>
                  {r.time === 9999 ? "—" : (() => {
                    const totalMs = r.time * 10;
                    const minutes = Math.floor(totalMs / 60000);
                    const seconds = Math.floor((totalMs % 60000) / 1000);
                    const centis = Math.floor((totalMs % 1000) / 10);
                    return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}.${centis.toString().padStart(2, "0")}`;
                  })()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </>
    )}
  </div>
)}

  return (
    <div className="modal-container">
      <h2>Pregunta {currentIndex + 1}</h2>
      <p>{current.question}</p>
      <div className="options">
        {current.options.map((opt, i) => (
          <button
            key={i}
            className={`option ${selected === i ? (i === current.answerIndex ? "correct" : "incorrect") : ""}`}
            onClick={() => handleAnswer(i)}
          >
            {opt}
          </button>
        ))}
      </div>
    </div>
  );
};

export default QuizGame;

// Evita error por función faltante
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function setStoredScore(_uint: number) {}